<template>
  <div class="user-management">
    <div class="toolbar" v-fade-in>
      <div class="toolbar-left">
        <button class="add-btn" @click="showAddModal = true" v-if="canCreate">添加用户</button>
        <button class="add-btn manager-btn" @click="showAddManagerModal = true" v-if="isAdmin">添加子管理员</button>
      </div>
      <div class="search-bar">
        <input
          v-model="searchQuery"
          type="text"
          class="search-input"
          placeholder="搜索账号 / ID / 创建者..."
          @input="onSearchInput"
        >
        <span class="search-icon" v-if="!searchQuery">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        </span>
        <span class="search-clear" v-else @click="clearSearch">✕</span>
        <span class="search-count" v-if="searchQuery">{{ filteredUsers.length }} / {{ users.length }}</span>
      </div>
    </div>

    <!-- PC端表格 -->
    <table class="user-table desktop-only" v-motion="{ initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.4, delay: 0.05 } }">
      <thead>
        <tr>
          <th>ID</th>
          <th>账号</th>
          <th>角色</th>
          <th>余额</th>
          <th>剩余天数</th>
          <th v-if="isAdmin">创建者</th>
          <th>操作</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="user in filteredUsers" :key="user.id">
          <td>{{ user.id }}</td>
          <td>{{ user.account }}</td>
          <td>
            <span :class="['role-tag', user.role]">{{ getRoleName(user.role) }}</span>
          </td>
          <td>
            <span class="balance">¥{{ user.balance || 0 }}</span>
          </td>
          <td>
            <span v-if="user.role === 'admin' || user.role === 'manager'" class="expire-status normal">永不过期</span>
            <span v-else-if="user.expireDays === 0" class="expire-status expired">已过期</span>
            <span v-else-if="user.expireDays <= 3" class="expire-status critical">剩余{{ user.expireDays }}天</span>
            <span v-else-if="user.expireDays <= 7" class="expire-status warning">剩余{{ user.expireDays }}天</span>
            <span v-else class="expire-status normal">剩余{{ user.expireDays }}天</span>
          </td>
          <td v-if="isAdmin">{{ user.createdBy || '-' }}</td>
          <td>
            <button class="action-btn edit" @click="openEditModal(user)" v-if="canUpdate">编辑</button>
            <button class="action-btn perm" @click="openPermModal(user)" v-if="isAdmin && user.role === 'manager'">权限</button>
            <button class="action-btn delete" @click="deleteUser(user.id)" v-if="canDelete && user.role !== 'admin'">删除</button>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- 搜索无结果提示 -->
    <div class="empty-search" v-if="searchQuery && filteredUsers.length === 0">
      <p>没有找到匹配「{{ searchQuery }}」的用户</p>
      <button class="clear-search-btn" @click="clearSearch">清除搜索</button>
    </div>

    <!-- 移动端卡片列表 -->
    <div class="user-cards mobile-only" v-motion="{ initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.4, delay: 0.05 } }">
      <div class="user-card" v-for="user in filteredUsers" :key="user.id">
        <div class="card-header">
          <span class="card-account">{{ user.account }}</span>
          <span :class="['role-tag', user.role]">{{ getRoleName(user.role) }}</span>
        </div>
        <div class="card-body">
          <div class="card-row">
            <span class="card-label">余额</span>
            <span class="balance">¥{{ user.balance || 0 }}</span>
          </div>
          <div class="card-row">
            <span class="card-label">状态</span>
            <span v-if="user.role === 'admin' || user.role === 'manager'" class="expire-status normal">永不过期</span>
            <span v-else-if="user.expireDays === 0" class="expire-status expired">已过期</span>
            <span v-else-if="user.expireDays <= 3" class="expire-status critical">剩余{{ user.expireDays }}天</span>
            <span v-else-if="user.expireDays <= 7" class="expire-status warning">剩余{{ user.expireDays }}天</span>
            <span v-else class="expire-status normal">剩余{{ user.expireDays }}天</span>
          </div>
          <div class="card-row" v-if="isAdmin">
            <span class="card-label">创建者</span>
            <span>{{ user.createdBy || '-' }}</span>
          </div>
        </div>
        <div class="card-actions">
          <button class="action-btn edit" @click="openEditModal(user)" v-if="canUpdate">编辑</button>
          <button class="action-btn perm" @click="openPermModal(user)" v-if="isAdmin && user.role === 'manager'">权限</button>
          <button class="action-btn delete" @click="deleteUser(user.id)" v-if="canDelete && user.role !== 'admin'">删除</button>
        </div>
      </div>
    </div>

    <!-- 添加用户弹窗 -->
    <div class="modal-overlay" v-if="showAddModal" @click.self="showAddModal = false">
      <div class="modal">
        <h3>添加用户</h3>
        <div class="form-group">
          <label>账号</label>
          <input v-model="newUser.account" type="text" placeholder="请输入账号">
        </div>
        <div class="form-group">
          <label>密码</label>
          <input v-model="newUser.password" type="text" placeholder="请输入密码">
        </div>
        <div class="form-group">
          <label>初始余额（元）</label>
          <input v-model.number="newUser.balance" type="number" step="0.01" min="0" placeholder="请输入初始余额（默认0）">
        </div>
        <div class="form-group">
          <label>有效期（天数）</label>
          <input v-model.number="newUser.expireDays" type="number" min="1" placeholder="请输入有效天数（默认30天）">
        </div>
        <div class="modal-actions">
          <button @click="showAddModal = false">取消</button>
          <button class="primary" @click="addUser">确定</button>
        </div>
      </div>
    </div>

    <!-- 添加子管理员弹窗 -->
    <div class="modal-overlay" v-if="showAddManagerModal" @click.self="showAddManagerModal = false">
      <div class="modal">
        <h3>添加子管理员</h3>
        <div class="form-group">
          <label>账号</label>
          <input v-model="newManager.account" type="text" placeholder="请输入账号">
        </div>
        <div class="form-group">
          <label>密码</label>
          <input v-model="newManager.password" type="text" placeholder="请输入密码">
        </div>
        <div class="form-group">
          <label>权限设置</label>
          <div class="perm-list">
            <label v-for="perm in availablePermissions" :key="perm.key" class="perm-item">
              <input type="checkbox" v-model="newManager.permissions" :value="perm.key">
              {{ perm.name }}
            </label>
          </div>
        </div>
        <div class="modal-actions">
          <button @click="showAddManagerModal = false">取消</button>
          <button class="primary" @click="addManager">确定</button>
        </div>
      </div>
    </div>

    <!-- 编辑用户弹窗 -->
    <div class="modal-overlay" v-if="showEditModal" @click.self="showEditModal = false">
      <div class="modal">
        <h3>编辑用户</h3>
        <div class="form-group">
          <label>账号</label>
          <input v-model="editUser.account" type="text" placeholder="修改用户名">
          <span v-if="editUser.account !== editUser.originalAccount" class="field-hint">修改用户名后该用户需要重新登录</span>
        </div>
        <div class="form-group">
          <label>新密码（留空则不修改）</label>
          <input v-model="editUser.newPassword" type="text" placeholder="请输入新密码">
        </div>
        <div class="form-group">
          <label>余额（元）</label>
          <input v-model.number="editUser.balance" type="number" step="0.01" min="0" placeholder="请输入余额">
        </div>
        <div class="form-group" v-if="editUser.role !== 'admin' && editUser.role !== 'manager'">
          <label>到期日期</label>
          <input v-model="editUser.expireAtDate" type="date">
        </div>
        <div class="modal-actions">
          <button @click="showEditModal = false">取消</button>
          <button class="primary" @click="updateUser">确定</button>
        </div>
      </div>
    </div>

    <!-- 权限管理弹窗 -->
    <div class="modal-overlay" v-if="showPermModal" @click.self="showPermModal = false">
      <div class="modal">
        <h3>权限管理 - {{ permUser.account }}</h3>
        <div class="form-group">
          <label>权限设置</label>
          <div class="perm-list">
            <label v-for="perm in availablePermissions" :key="perm.key" class="perm-item">
              <input type="checkbox" v-model="permUser.permissions" :value="perm.key">
              {{ perm.name }}
            </label>
          </div>
        </div>
        <div class="modal-actions">
          <button @click="showPermModal = false">取消</button>
          <button class="primary" @click="savePermissions">保存</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiFetch } from '@/config/api'

export default {
  name: 'UserManagement',
  data() {
    return {
      users: [],
      searchQuery: '',
      showAddModal: false,
      showAddManagerModal: false,
      showEditModal: false,
      showPermModal: false,
      isAdmin: false,
      myPermissions: '',
      availablePermissions: [
        { key: 'user:create', name: '创建用户' },
        { key: 'user:read', name: '查看用户' },
        { key: 'user:update', name: '修改用户' },
        { key: 'user:delete', name: '删除用户' },
        { key: 'api:create', name: '添加接口' },
        { key: 'api:read', name: '查看接口' },
        { key: 'api:update', name: '修改接口' },
        { key: 'api:delete', name: '删除接口' }
      ],
      newUser: {
        account: '',
        password: '',
        expireDays: 30,
        balance: 0
      },
      newManager: {
        account: '',
        password: '',
        permissions: []
      },
      editUser: {
        id: null,
        account: '',
        originalAccount: '',
        role: '',
        newPassword: '',
        expireDays: 0,
        expireAtDate: '',
        balance: 0
      },
      permUser: {
        id: null,
        account: '',
        permissions: []
      }
    }
  },
  computed: {
    filteredUsers() {
      if (!this.searchQuery.trim()) return this.users
      const q = this.searchQuery.trim().toLowerCase()
      return this.users.filter(user => {
        const account = (user.account || '').toLowerCase()
        const id = String(user.id || '')
        const role = this.getRoleName(user.role).toLowerCase()
        const createdBy = (user.createdBy || '').toLowerCase()
        return account.includes(q) || id.includes(q) || role.includes(q) || createdBy.includes(q)
      })
    },
    canCreate() {
      return this.isAdmin || (this.myPermissions && this.myPermissions.includes('user:create'))
    },
    canUpdate() {
      return this.isAdmin || (this.myPermissions && this.myPermissions.includes('user:update'))
    },
    canDelete() {
      return this.isAdmin || (this.myPermissions && this.myPermissions.includes('user:delete'))
    }
  },
  mounted() {
    this.fetchMyPermissions()
    this.fetchUsers()
  },
  methods: {
    onSearchInput() {
      // 实时过滤，通过 computed filteredUsers 自动处理
    },
    clearSearch() {
      this.searchQuery = ''
    },
    getRoleName(role) {
      const names = { admin: '超级管理员', manager: '子管理员', user: '普通用户' }
      return names[role] || role
    },
    async fetchMyPermissions() {
      try {
        const res = await apiFetch('/api/admin/users/my-permissions')
        if (res.code === 0) {
          this.isAdmin = res.data.isAdmin
          this.myPermissions = res.data.permissions || ''
        }
      } catch (e) {
        console.error('获取权限失败', e)
      }
    },
    async fetchUsers() {
      try {
        const res = await apiFetch('/api/admin/users')
        if (res.code === 0) {
          this.users = res.data.map(u => ({
            ...u,
            expireDays: this.calculateExpireDays(u.expireAt, u.role)
          }))
        }
      } catch (e) {
        console.error('获取用户列表失败', e)
      }
    },
    calculateExpireDays(expireAt, role) {
      if (role === 'admin' || role === 'manager') return -1
      if (!expireAt) return 0
      const end = new Date(expireAt)
      const now = new Date()
      const diff = end - now
      if (diff < 0) return 0
      return Math.floor(diff / (1000 * 60 * 60 * 24))
    },
    openEditModal(user) {
      this.editUser = {
        id: user.id,
        account: user.account,
        originalAccount: user.account,
        role: user.role,
        newPassword: '',
        expireDays: user.expireDays,
        expireAtDate: user.expireAt ? user.expireAt.split('T')[0] : '',
        balance: user.balance || 0
      }
      this.showEditModal = true
    },
    openPermModal(user) {
      this.permUser = {
        id: user.id,
        account: user.account,
        permissions: user.permissions ? user.permissions.split(',') : []
      }
      this.showPermModal = true
    },
    async savePermissions() {
      try {
        const res = await apiFetch(`/api/admin/users/${this.permUser.id}/permissions`, {
          method: 'PUT',
          body: JSON.stringify({ permissions: this.permUser.permissions.join(',') })
        })
        if (res.code === 0) {
          alert('权限保存成功')
          this.showPermModal = false
          this.fetchUsers()
        } else {
          alert(res.msg || '保存失败')
        }
      } catch (e) {
        alert('保存失败')
      }
    },
    async updateUser() {
      try {
        const updateData = {}
        // 如果账号有改动，发送新账号
        if (this.editUser.account && this.editUser.account !== this.editUser.originalAccount) {
          updateData.account = this.editUser.account.trim()
        }
        if (this.editUser.newPassword) {
          updateData.password = this.editUser.newPassword
        }
        if (this.editUser.balance !== undefined && this.editUser.balance !== null) {
          updateData.balance = this.editUser.balance
        }
        if (this.editUser.role !== 'admin' && this.editUser.role !== 'manager' && this.editUser.expireAtDate) {
          const expireDate = new Date(this.editUser.expireAtDate + 'T23:59:59')
          if (!isNaN(expireDate.getTime())) {
            updateData.expireAt = expireDate.toISOString()
          }
        }
        const res = await apiFetch(`/api/admin/users/${this.editUser.id}`, {
          method: 'PUT',
          body: JSON.stringify(updateData)
        })
        if (res.code === 0) {
          alert('更新成功')
          this.showEditModal = false
          this.fetchUsers()
        } else {
          alert(res.msg || '更新失败')
        }
      } catch (e) {
        alert('更新失败')
      }
    },
    async addUser() {
      try {
        const userData = {
          account: this.newUser.account,
          password: this.newUser.password,
          role: 'user'
        }
        if (this.newUser.balance > 0) {
          userData.balance = this.newUser.balance
        }
        if (this.newUser.expireDays > 0) {
          const expireDate = new Date()
          expireDate.setDate(expireDate.getDate() + this.newUser.expireDays)
          expireDate.setHours(23, 59, 59, 999)
          userData.expireAt = expireDate.toISOString()
        }
        const res = await apiFetch('/api/admin/users', {
          method: 'POST',
          body: JSON.stringify(userData)
        })
        if (res.code === 0) {
          alert('添加成功')
          this.showAddModal = false
          this.newUser = { account: '', password: '', expireDays: 30, balance: 0 }
          this.fetchUsers()
        } else {
          alert(res.msg)
        }
      } catch (e) {
        alert('添加失败')
      }
    },
    async addManager() {
      try {
        const userData = {
          account: this.newManager.account,
          password: this.newManager.password,
          role: 'manager',
          permissions: this.newManager.permissions.join(',')
        }
        const res = await apiFetch('/api/admin/users', {
          method: 'POST',
          body: JSON.stringify(userData)
        })
        if (res.code === 0) {
          alert('添加成功')
          this.showAddManagerModal = false
          this.newManager = { account: '', password: '', permissions: [] }
          this.fetchUsers()
        } else {
          alert(res.msg)
        }
      } catch (e) {
        alert('添加失败')
      }
    },
    async deleteUser(id) {
      if (!confirm('确定删除该用户吗？')) return
      try {
        const res = await apiFetch(`/api/admin/users/${id}`, { method: 'DELETE' })
        if (res.code === 0) {
          this.fetchUsers()
        } else {
          alert(res.msg || '删除失败')
        }
      } catch (e) {
        alert('删除失败')
      }
    }
  }
}
</script>

<style scoped>
.user-management { padding: 0; }
.toolbar { margin-bottom: 16px; display: flex; gap: 8px; align-items: center; justify-content: space-between; flex-wrap: wrap; }
.toolbar-left { display: flex; gap: 8px; }
.add-btn { background: #111; color: #fff; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer; font-size: 13px; font-weight: 500; }
.add-btn:hover { background: #333; }
.manager-btn { background: #555; }
.manager-btn:hover { background: #333; }
.search-bar { position: relative; display: flex; align-items: center; }
.search-input { width: 220px; padding: 8px 32px 8px 12px; border: 1px solid #ddd; border-radius: 4px; font-size: 13px; background: #fff; transition: border-color 0.2s, box-shadow 0.2s; }
.search-input:focus { outline: none; border-color: #111; box-shadow: 0 0 0 2px rgba(17, 17, 17, 0.08); }
.search-input::placeholder { color: #bbb; }
.search-icon { position: absolute; right: 10px; color: #bbb; display: flex; align-items: center; pointer-events: none; }
.search-clear { position: absolute; right: 10px; color: #999; cursor: pointer; font-size: 12px; display: flex; align-items: center; justify-content: center; width: 18px; height: 18px; border-radius: 50%; transition: all 0.15s; }
.search-clear:hover { background: #f0f0f0; color: #333; }
.search-count { position: absolute; right: -70px; font-size: 12px; color: #999; white-space: nowrap; }
.user-table { width: 100%; border-collapse: collapse; background: #fff; border: 1px solid #e8e8e8; }
.user-table th, .user-table td { padding: 10px 12px; text-align: left; border-bottom: 1px solid #f0f0f0; font-size: 13px; }
.user-table th { background: #fafafa; font-weight: 600; font-size: 12px; color: #666; text-transform: uppercase; letter-spacing: 0.3px; }
.user-table tbody tr:hover { background: #fafafa; }
.role-tag { padding: 2px 8px; border-radius: 3px; font-size: 12px; border: 1px solid; }
.role-tag.admin { background: #fff; color: #111; border-color: #111; font-weight: 600; }
.role-tag.manager { background: #fff; color: #555; border-color: #999; }
.role-tag.user { background: #fff; color: #999; border-color: #ddd; }
.action-btn { border: none; background: none; cursor: pointer; padding: 4px 8px; margin-right: 6px; font-size: 12px; border-radius: 3px; }
.action-btn.edit { color: #111; border: 1px solid #ddd; }
.action-btn.edit:hover { background: #111; color: #fff; border-color: #111; }
.action-btn.perm { color: #555; border: 1px solid #ddd; }
.action-btn.perm:hover { background: #555; color: #fff; border-color: #555; }
.action-btn.delete { color: #999; border: 1px solid #ddd; }
.action-btn.delete:hover { background: #111; color: #fff; border-color: #111; }
.modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.modal { background: #fff; padding: 24px; width: 400px; max-height: 80vh; overflow-y: auto; border: 1px solid #e8e8e8; }
.modal h3 { margin: 0 0 20px; font-size: 16px; font-weight: 600; color: #111; }
.form-group { margin-bottom: 16px; }
.form-group label { display: block; margin-bottom: 6px; font-size: 13px; color: #666; }
.form-group input, .form-group select { width: 100%; padding: 9px 12px; border: 1px solid #ddd; border-radius: 4px; box-sizing: border-box; font-size: 14px; }
.form-group input:focus, .form-group select:focus { outline: none; border-color: #111; }
.form-group input:disabled { background-color: #f5f5f5; cursor: not-allowed; }
.field-hint { display: block; margin-top: 4px; font-size: 12px; color: #e67e22; }
.perm-list { border: 1px solid #ddd; border-radius: 4px; padding: 10px; max-height: 200px; overflow-y: auto; }
.perm-item { display: flex; align-items: center; gap: 8px; padding: 5px 0; cursor: pointer; font-size: 13px; }
.perm-item input[type="checkbox"] { width: auto; }
.modal-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 20px; }
.modal-actions button { padding: 8px 16px; border: 1px solid #ddd; background: #fff; border-radius: 4px; cursor: pointer; font-size: 13px; }
.modal-actions button:hover { background: #f5f5f5; }
.modal-actions button.primary { background: #111; color: #fff; border-color: #111; }
.modal-actions button.primary:hover { background: #333; }
.expire-status { padding: 2px 8px; border-radius: 3px; font-size: 12px; font-weight: 500; }
.expire-status.normal { background: #f5f5f5; color: #111; }
.expire-status.warning { background: #f5f5f5; color: #666; }
.expire-status.critical { background: #f5f5f5; color: #333; font-weight: 600; }
.expire-status.expired { background: #f5f5f5; color: #bbb; text-decoration: line-through; }
.balance { color: #111; font-weight: 500; }
.empty-search { text-align: center; padding: 40px 20px; color: #999; font-size: 14px; }
.empty-search p { margin: 0 0 12px; }
.clear-search-btn { border: 1px solid #ddd; background: #fff; color: #666; padding: 6px 16px; border-radius: 4px; cursor: pointer; font-size: 13px; }
.clear-search-btn:hover { background: #f5f5f5; border-color: #111; color: #111; }
.mobile-only { display: none; }
.user-cards { display: flex; flex-direction: column; gap: 12px; }
.user-card { background: #fff; padding: 16px; border: 1px solid #e8e8e8; }
.card-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; padding-bottom: 12px; border-bottom: 1px solid #f0f0f0; }
.card-account { font-weight: 600; font-size: 15px; color: #111; }
.card-body { margin-bottom: 12px; }
.card-row { display: flex; justify-content: space-between; align-items: center; padding: 6px 0; font-size: 13px; }
.card-label { color: #999; }
.card-actions { display: flex; gap: 8px; padding-top: 12px; border-top: 1px solid #f0f0f0; }
.card-actions .action-btn { flex: 1; padding: 8px; text-align: center; }
@media screen and (max-width: 768px) {
  .desktop-only { display: none; }
  .mobile-only { display: block; }
  .toolbar { flex-direction: column; }
  .toolbar-left { flex-direction: column; width: 100%; }
  .add-btn { width: 100%; padding: 11px; }
  .search-bar { width: 100%; }
  .search-input { width: 100%; box-sizing: border-box; }
  .search-count { position: static; margin-left: 8px; }
  .modal { width: 90%; max-width: 400px; margin: 20px; }
  .modal-actions { flex-direction: column; gap: 8px; }
  .modal-actions button { width: 100%; padding: 11px; }
}
</style>
